import { useCallback, useState } from "react";
import { usePipelineStatusesQuery } from "#graphql/supergraphHooks";
import { PAGE_SIZE, afterCursorForPage } from "./pagination";
import type { PipelineStatusesQuery } from "#graphql/supergraphTypes";
import type { ApolloError } from "@apollo/client";

export type PipelineStatusRow =
  PipelineStatusesQuery["pipelineStatuses"]["edges"][number]["node"];

export interface UsePipelineStatusesResult {
  statuses: PipelineStatusRow[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => Promise<unknown>;
  page: number;
  totalPages: number;
  // Accepts null so it can be passed straight to NumberedPagination's
  // handleClick (which emits null for the non-navigating ellipsis item).
  setPage: (page: number | null) => void;
}

export function usePipelineStatuses(): UsePipelineStatusesResult {
  const [page, setPageState] = useState(1);

  // cache-and-network: render cached rows immediately but always revalidate.
  // notifyOnNetworkStatusChange: flip `loading` during a page change / refetch.
  const { data, previousData, loading, error, refetch } = usePipelineStatusesQuery({
    variables: { first: PAGE_SIZE, after: afterCursorForPage(page) },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  // While fetching an uncached page, `data` is undefined; fall back to
  // previousData so the total and rows stay stable across page changes.
  const connection = data?.pipelineStatuses ?? previousData?.pipelineStatuses;
  const totalCount = connection?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const setPage = useCallback(
    (next: number | null) => {
      if (next == null) return; // NumberedPagination emits null for the ellipsis; ignore it.
      setPageState(Math.min(Math.max(next, 1), totalPages));
    },
    [totalPages],
  );

  // Clamp during render (not an effect) only once a real total is known, so an
  // in-flight page fetch (connection momentarily absent) doesn't bounce to page 1.
  if (connection && page > totalPages) {
    setPageState(totalPages);
  }

  const statuses = connection?.edges.map((edge) => edge.node) ?? [];

  return { statuses, loading, error, refetch, page, totalPages, setPage };
}
